
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import IconButton from "@/components/UI/IconButton.vue";
import Icon from "@/components/UI/Icon.vue";
import {
  SectionCardData,
  getAllSections,
  getAllSectionsCategory
} from "@/lib/singularity/section-add-or-swap";
import SectionCard from "./SectionCard.vue";
import { handleSectionChosen } from "@/lib/singularity/section-add-or-swap";
import { singularityStore } from "@/store/store";
import { TreeNode } from "@/types/tree";
import { findNode } from "@/lib/tree/tree";
import { getPromptInfoFromNode } from "@/lib/singularity/util";
import { documentStore } from "@/store/store";
import SingularityOption from "../components/SingularityOption.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import { logEvent } from "@/util/tracking";
import { debounce } from "lodash";

enum gridView {
  showAll = "all",
  selected = "selected"
}

@Component({
  components: { IconButton, Icon, SectionCard, SingularityOption, NiceButton }
})
export default class SingularitySectionLibraryModal extends Vue {
  searchCategory = "";
  columnLayout = 2;
  gridView = gridView.showAll;
  selectedSectionIndex: number | null = null;
  selectedSection: SectionCardData | null = null;

  setGridView(view: gridView) {
    this.gridView = view;
  }

  setGridViewToAll() {
    this.selectedSection = null;
    this.selectedSectionIndex = null;
    this.gridView = gridView.showAll;
  }

  get modalTitle() {
    if (this.isEditingHeaderOrFooter) {
      if (this.headerAndFooterEditorPanelState?.elementType === "header") {
        return "Add Header";
      }
      if (this.headerAndFooterEditorPanelState?.elementType === "footer") {
        return "Add Footer";
      }
    }

    if (this.gridView === gridView.selected) {
      return "Insert Section";
    }

    return "Add Section";
  }

  closeDialog() {
    this.$emit("close");
  }

  get sectionToSwapData() {
    if (!this.sectionAddOrSwapPanelState.sectionId) return null;
    const sectionToSwap = findNode(
      documentStore.document.tree,
      this.sectionAddOrSwapPanelState.sectionId
    );
    if (!sectionToSwap) return null;

    return getPromptInfoFromNode(sectionToSwap).singularity.sectionData;
  }

  currentSectionIsSame(index: number, type: string) {
    return (
      this.sectionToSwapData?.type === type &&
      this.sectionToSwapData?.index === index
    );
  }

  get gridClass() {
    return `singularity-section-library-modal__sections-masonry singularity-section-library-modal__sections-masonry--${this.columnLayout}`;
  }

  setColumnLayout(columns: number) {
    this.columnLayout = columns;
  }

  get sections() {
    return getAllSections();
  }

  setSelectedSection(section: SectionCardData, index: number) {
    this.setGridView(gridView.selected);
    this.selectedSectionIndex = index;
    this.selectedSection = section;
  }

  get isReplacingSection() {
    return this.mode === 'swap';
  }

  handleSection(rewrite: boolean) {
    if (
      !this.selectedSection ||
      this.selectedSectionIndex === null ||
      this.selectedSectionIndex === undefined
    )
      return;

    logEvent("FutureLayer Section Chosen", {
      sectionType: this.selectedSection.type,
      sectionIndex: this.selectedSectionIndex,
      mode: this.mode,
      isRewriteActive: rewrite
    });

    handleSectionChosen(
      this.selectedSection,
      this.selectedSectionIndex,
      this.sectionAddSectionId,
      this.mode,
      rewrite
    );
    this.closeDialog();
  }

  get sectionAddOrSwapPanelState() {
    return singularityStore.sectionAddOrSwapPanelState;
  }

  get sectionAddSectionId(): TreeNode["id"] {
    return this.sectionAddOrSwapPanelState.sectionId ?? -1;
  }

  get mode() {
    return this.sectionAddOrSwapPanelState.mode;
  }

  get headerAndFooterEditorPanelState() {
    return singularityStore.headerAndFooterEditorPanelState;
  }

  get isEditingHeaderOrFooter() {
    return this.headerAndFooterEditorPanelState !== null;
  }

  get filteredSections() {
    if (this.isEditingHeaderOrFooter) {
      return ["Headers", "Footer", "Index"];
    }
    return ["Index"];
  }

  get activeCategory() {
    return this.sections.filter(
      category => category.type === this.activeCategoryType
    );
  }

  get sectionsCategories() {
    // Always hide "Index"
    const allCategories = getAllSectionsCategory().filter(
      category => category.type !== "Index"
    );

    // If editing header/footer, only show Headers/Footers
    if (this.isEditingHeaderOrFooter) {
      if (this.headerAndFooterEditorPanelState?.elementType === "header") {
        return allCategories
          .filter(category => category.type === "Headers")
          .filter(category =>
            category.type
              .toLowerCase()
              .includes(this.searchCategory.toLowerCase().trim())
          );
      }

      if (this.headerAndFooterEditorPanelState?.elementType === "footer") {
        return allCategories
          .filter(category => category.type === "Footers")
          .filter(category =>
            category.type
              .toLowerCase()
              .includes(this.searchCategory.toLowerCase().trim())
          );
      }

      return allCategories
        .filter(category => ["Headers", "Footers"].includes(category.type))
        .filter(category =>
          category.type
            .toLowerCase()
            .includes(this.searchCategory.toLowerCase().trim())
        );
    }

    // Otherwise, hide Headers/Footers
    return allCategories
      .filter(category => !["Headers", "Footers"].includes(category.type))
      .filter(category =>
        category.type
          .toLowerCase()
          .includes(this.searchCategory.toLowerCase().trim())
      );
  }

  selectedCategoryType: string | null = null;

  get activeCategoryType() {
    if (!this.selectedCategoryType && this.sectionToSwapData?.type) {
      this.selectedCategoryType = this.sectionToSwapData.type;
    }
    return this.selectedCategoryType || "Features";
  }

  setSelectCategory(categoryType: string) {
    this.selectedCategoryType = categoryType;
    logEvent("FutureLayer Section Category Selected", {
      categoryType
    });
  }

  @Watch("searchCategory")
  onSearchCategoryChange(value: string) {
    if (value === "") return;
    this.debouncedLogSearchFn(value);
  }

  debouncedLogSearchFn = debounce((value: string) => {
    logEvent("FutureLayer Section Search", {
      searchTerm: value
    });
  }, 1000);
}
